<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { HelpCircleIcon, Settings01Icon, XCloseIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIInputNumber,
  UIPopover,
  UISelect,
  UITextMdMedium,
  UITextSmMedium,
  UITextSmRegular,
  UITextXsMedium,
} from "@gohighlevel/ghl-ui";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import CategoryExplanation from "../Category/CategoryExplanation.vue";
import FederatedManageCategoryModal from "../Category/FederatedManageCategoryModal.vue";
const store = useAppStore();

const { t } = useI18n();
const props = defineProps({
  index: {
    type: Number,
    default: 0,
  },
  selectedCategory: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const emit = defineEmits(["update:value"]);

const optionCategory = ref(
  store.category.map((cat) => {
    return { label: cat.label, value: cat.id };
  })
);
const score = ref(props.selectedCategory.score || 0);

const isAddNewEnabled = ref(false);

const value = ref(store.category.find((cat) => cat.id === props.selectedCategory.categoryId)?.id);

const handleSelect = (val: string) => {
  const category = store.category.find((cat) => cat.id === val);
  emit("update:value", {
    category: category.value,
    index: props.index,
    score: score.value,
    categoryId: category.id,
  });
  value.value = val;
  store.addToHistory();
};

const addNew = () => {
  isAddNewEnabled.value = true;
};

const updateScore = (newScore) => {
  score.value = newScore;
  const category = store.category.find((cat) => cat.id === value.value);
  emit("update:value", {
    categoryId: value.value,
    index: props.index,
    score: score.value,
    category: category.value,
  });
};
const popoverShow = ref(false);

const closeModal = () => {
  popoverShow.value = false;
};

const handleClickOutside = () => {
  closeModal();
};
</script>

<template>
  <div class="flex items-start gap-2 self-stretch">
    <div class="w-full">
      <UITextSmMedium class="flex items-center">
        {{ t("quiz.category.category") }}
        <UIPopover
          header-class="category-popover !pt-6 border-0"
          :show="popoverShow"
          :disabled="false"
          :flip="true"
          :overlap="false"
          :width="580"
          :raw="false"
          placement="left-start"
          :scrollable="false"
          class="!rounded-xl"
          trigger="manual"
          @clickoutside="handleClickOutside"
        >
          <template #header>
            <div class="flex flex-col items-start gap-2.5 self-stretch px-[10px]">
              <div class="flex items-center justify-between self-stretch">
                <UITextMdMedium class="text-gray-900">{{
                  t("quiz.category.category")
                }}</UITextMdMedium>
                <XCloseIcon
                  class="h-6 w-6 cursor-pointer text-gray-500 hover:text-gray-700"
                  @click="closeModal"
                />
              </div>
            </div>
          </template>
          <template #trigger>
            <HelpCircleIcon
              class="ml-1 mt-0.5 h-4 w-4 cursor-pointer text-gray-400 hover:text-gray-600"
              @click="popoverShow = !popoverShow"
            />
          </template>
          <div>
            <CategoryExplanation />
          </div>
        </UIPopover>
      </UITextSmMedium>
      <UISelect
        id="category-picker"
        size="large"
        :value="value"
        :options="optionCategory"
        :filterable="true"
        @change="handleSelect"
      >
        <template #action>
          <div
            v-if="!isAddNewEnabled"
            class="flex cursor-pointer items-center justify-center gap-1"
            @click="addNew()"
          >
            <Settings01Icon class="h-4 w-4" />
            <UITextSmRegular>{{ t("quiz.category.addManage") }}</UITextSmRegular>
          </div>
        </template>
      </UISelect>
      <FederatedManageCategoryModal
        :show="isAddNewEnabled"
        @update:close-modal="isAddNewEnabled = false">
      </FederatedManageCategoryModal>
    </div>
    <div class="w-[100px]">
      <UITextXsMedium class="flex">{{ t("fields.customized.score") }}</UITextXsMedium>
      <UIInputNumber
        id="score"
        :model-value="score"
        :min="0"
        size="large"
        placeholder="10"
        :disabled="!value"
        :show-button="false"
        @update:model-value="updateScore"
      ></UIInputNumber>
    </div>
  </div>
</template>

<style scoped>
.category-popover {
  padding: 0;
}
</style>
